import { ORDERED_PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDate, formatDateTime, formatMoney, formatQuantity } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

type OrderedPartBasicInfoProps = {
  previewId?: string | number;
};

export const OrderedPartBasicInfo = ({ previewId }: OrderedPartBasicInfoProps = {}) => {
  const { id, orderedPartId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(ORDERED_PARTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [orderedPart, setOrderedPart] = useState<any | null>(null);
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");
  const entityId = previewId ?? orderedPartId;

  useEffect(() => {
    const fetchOrderedPart = async () => {
      if (entityId) {
        const response = await get(entityId);
        if (response.data) {
          setOrderedPart(response.data);
          // Breadcrumbs reflect the real routed page only - skip when rendering as a preview.
          if (!previewId && id) {
            setOverride(`/service/stock-status/${id}/ordered-parts/${entityId}`, response.data.number || entityId);
          }
        }
      }
    };

    const handleOrderedPartUpdated = () => {
      fetchOrderedPart();
    };

    window.addEventListener("orderedPartUpdated", handleOrderedPartUpdated);
    fetchOrderedPart();
    return () => {
      window.removeEventListener("orderedPartUpdated", handleOrderedPartUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewOrderedParts) {
    return null;
  }

  if (!orderedPart) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {orderedPart.number || id}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Number")}>
            <Typography variant="subtitle1">{orderedPart.number || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Status")}>
            <Typography variant="subtitle1">{orderedPart.status_display || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Part")}>
            <Typography variant="subtitle1">{orderedPart.part_name || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Availability")}>
            <Typography variant="subtitle1">
              {formatQuantity(orderedPart.availability ?? 0)} / {formatQuantity(orderedPart.quantity ?? 0)}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Price Net")}>
            <Typography variant="subtitle1">
              {formatMoney(orderedPart.price_net ?? 0)} (+{formatQuantity(orderedPart.vat ?? 0)}% {t("VAT")})
            </Typography>
          </DetailRow>
          <DetailRow label={t("Total Cost")}>
            <Typography variant="subtitle1">{formatMoney(orderedPart.total ?? 0)}</Typography>
          </DetailRow>
          {orderedPart.delivered_at && (
            <DetailRow label={t("Delivered At")}>
              <Typography variant="subtitle1">{formatDate(orderedPart.delivered_at)}</Typography>
            </DetailRow>
          )}
          <DetailRow label={t("Ordered By")} noBorder>
            <Typography variant="subtitle1">
              {orderedPart.ordered_by ? (
                [orderedPart.ordered_by.first_name, orderedPart.ordered_by.last_name]
                  .filter(Boolean)
                  .join(" ") || orderedPart.ordered_by.email || "-"
              ) : "-"}
            </Typography>
          </DetailRow>
        </Box>
      </Box>

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(orderedPart.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(orderedPart.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
